import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency, fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SupplierInvoiceSheet } from "@/components/billing/SupplierInvoiceSheet";
import { PeriodPicker, usePeriod } from "@/components/billing/PeriodPicker";

export const STATE = { da_abbinare: ["Da abbinare", "amber"], parziale: ["Parziale", "blue"], abbinata: ["Abbinata", "green"] };
const FILTERS = [["", "Tutte"], ["da_abbinare", "Da abbinare"], ["parziale", "Parziali"], ["abbinata", "Abbinate"]];

const Row = ({ it, onOpen }) => (
  <li className="flex cursor-pointer flex-wrap items-center gap-3 py-3 hover:bg-slate-50" onClick={() => onOpen(it.id)} data-testid={`supplier-invoice-${it.id}`}>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-ink">{it.supplier_name} <span className="font-normal text-slate-500">· fatt. {it.number || "s.n."} del {fmtDate(it.date)}</span></p>
      <p className="text-xs text-slate-500">{it.lines_matched}/{it.lines_total} righe abbinate{it.category ? ` · ${it.category}` : ""}{it.next_due_date ? ` · scad. ${fmtDate(it.next_due_date)}` : ""}</p>
    </div>
    <StatusBadge tone={STATE[it.state][1]}>{STATE[it.state][0]}</StatusBadge>
    <div className="w-28 text-right"><p className="text-sm font-bold text-ink">{fmtCurrency(it.amount_net)}</p><p className="text-[11px] text-slate-400">imponibile</p></div>
  </li>
);

export default function SupplierInvoicesPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("da_abbinare");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const per = usePeriod();
  const load = useCallback(() => {
    api.get("/supplier-invoices", { params: { ...JSON.parse(per.rangeKey), ...(state ? { state } : {}), ...(q.trim() ? { q: q.trim() } : {}) } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [state, q, per.rangeKey]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const sync = async () => {
    setBusy(true);
    try { const r = (await api.post("/supplier-invoices/sync", null, { timeout: 180000 })).data; toast.success(`Aggiornate: ${r.new} nuove, ${r.updated} aggiornate`); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="supplier-invoices-page">
      <PageHeader overline="Amministrazione · ciclo passivo" title="Fatture fornitori"
        description="Fatture ricevute in Fatture in Cloud, importate ogni notte. Abbina ogni riga alla riga dell'ordine di vendita per vedere il margine reale."
        actions={<Button onClick={sync} disabled={busy} className="gap-2" data-testid="supplier-invoices-sync"><RefreshCw className={busy ? "h-4 w-4 animate-spin" : "h-4 w-4"} />Aggiorna</Button>} />
      <Panel className="p-4 md:p-5">
        <PeriodPicker {...per} testId="supplier-invoices" />
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => <Button key={k} size="sm" variant={state === k ? "default" : "outline"} className="rounded-full" onClick={() => setState(k)} data-testid={`supplier-filter-${k || "all"}`}>{l}</Button>)}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca fornitore, numero, articolo" className="ml-auto h-9 w-64" data-testid="supplier-invoices-search" />
        </div>
        {data && <p className="mb-2 text-xs text-slate-500" data-testid="supplier-invoices-summary"><span className="capitalize">{per.range.label}</span> · {data.summary.count} fatture · {fmtCurrency(data.summary.net)} imponibile · {data.summary.to_match} da completare
          {data.sync?.synced_at ? ` · ultimo aggiornamento ${fmtDateTime(data.sync.synced_at)}` : " · mai aggiornato"}{data.sync?.error ? ` · errore: ${data.sync.error}` : ""}</p>}
        <ul className="divide-y divide-line">{(data?.items || []).map((it) => <Row key={it.id} it={it} onOpen={setOpen} />)}</ul>
        {data && !data.items.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="supplier-invoices-empty">Nessuna fattura nel periodo. Premi «Aggiorna» per importarle da Fatture in Cloud.</p>}
      </Panel>
      <SupplierInvoiceSheet id={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
